import { useEffect, useMemo } from 'react';
import {
  Control,
  FieldValues,
  useForm,
  useFormState,
  FieldError,
} from 'react-hook-form';
import { get } from 'lodash';

import {
  SelectField,
  CustomComponents,
  Country,
  COUNTRIES,
} from '@electricjs/arc';

import { useTranslation } from 'react-i18next';

import { getCountryName } from '@/helpers/getCountryName';
import getFormattingLocale from '@/helpers/getFormattingLocale';
import CustomCountryOption from './CustomCountryOption';

export type CountrySelectorProps<T extends FieldValues = FieldValues> = {
  name: string;
  onChange: (value: string) => void;
  value: string;
  required?: boolean;
  disabled?: boolean;
  sortAlphabetically?: boolean;
  showUsaFirst?: boolean;
  showPhoneCode?: boolean;
  control: Control<T>;
};

const CountrySelector = <T extends FieldValues = FieldValues>({
  name,
  value,
  onChange,
  required = false,
  disabled = false,
  sortAlphabetically = true,
  showUsaFirst = true,
  showPhoneCode = false,
  control: formControl,
}: CountrySelectorProps<T>) => {
  const { t } = useTranslation('common');
  // We need to use a separate form instance to keep the field isolated from the parent form shape.
  // We use the onChange prop to update the parent form value as a string (ISO code)
  const { control, clearErrors, setError, setValue } = useForm();
  // Get the errors from the parent form
  const { errors: formErrors } = useFormState({ control: formControl });
  const customError = get(formErrors, name) as FieldError | undefined;

  const currentLocale = getFormattingLocale();

  // dropdown options (label/value pairs)
  const options = useMemo(() => {
    const countriesCodes = Object.keys(COUNTRIES) as Country[]; // get all country codes

    let countries = countriesCodes.map(code => ({
      translatedName: getCountryName(code), // translated country name
      code,
    }));

    if (sortAlphabetically) {
      countries.sort((a, b) =>
        a.translatedName.localeCompare(b.translatedName, currentLocale)
      );
    }

    if (showUsaFirst) {
      const usa = countries.find(country => country.code === 'US');
      if (usa) {
        countries = [
          { ...usa },
          ...countries.filter(country => country.code !== 'US'),
        ];
      }
    }

    return countries.map(country => ({
      label: `${COUNTRIES[country.code]?.flag} ${country.translatedName}`,
      value: country.code,
    }));
  }, [sortAlphabetically, showUsaFirst, currentLocale]);

  useEffect(() => {
    setValue(
      name,
      options.find(option => option.value === value),
      {
        shouldValidate: true,
      }
    );
  }, [value, name, setValue, options]);

  // Handle custom error
  useEffect(() => {
    if (customError && Object.values(customError).length > 0) {
      setError(name, {
        type: customError.type,
        message: customError.message,
      });
    } else {
      clearErrors(name);
    }
  }, [customError, name, setError, clearErrors]);

  const customComponents: CustomComponents = useMemo(() => {
    return {
      Option: option => {
        const country = COUNTRIES[option.data.value as Country];
        if (!country) {
          return null;
        }
        return (
          <CustomCountryOption
            country={country}
            isSelected={option.isSelected}
            showPhoneCode={showPhoneCode}
          />
        );
      },
    };
  }, [showPhoneCode]);

  return (
    <SelectField
      id="country-selector"
      name={name}
      control={control}
      label={t('countrySelector.label')}
      required={required}
      disabled={disabled}
      placeholder={t('countrySelector.placeholder')}
      options={options}
      onChange={(option, meta) => {
        // Needed to empty the field when the user clicks the clear button
        if (meta.action === 'clear') {
          onChange?.('');
        } else if (option?.value) {
          onChange?.(option.value);
        }

        setValue(name, option, {
          shouldValidate: true,
        });
      }}
      customComponents={customComponents}
      isSearchable
      isClearable
    />
  );
};

export default CountrySelector;
